import React from "react";
import { motion } from "framer-motion";
import { APPLY_URL } from "./constants";
import { ArrowUpRight } from "lucide-react";
import FlowerOfLife from "./FlowerOfLife";

const Hero = () => {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] flex items-end overflow-hidden"
        >
            {/* Cosmic gradient background */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(120% 80% at 20% 10%, #1a1e3d 0%, #0a0c27 45%, #060818 100%)",
                }}
                aria-hidden="true"
            />
            {/* Starfield */}
            <div
                className="absolute inset-0 dn-stars opacity-90"
                aria-hidden="true"
            />

            {/* Flower of Life — large, rotating, gold, centered right */}
            <div
                className="absolute -right-20 md:right-[-6%] top-1/2 -translate-y-1/2 w-[120vmin] h-[120vmin] md:w-[95vmin] md:h-[95vmin] opacity-[0.35] md:opacity-[0.55] dn-gold-glow"
                aria-hidden="true"
            >
                <div className="absolute inset-0 dn-rotate-slow">
                    <FlowerOfLife color="#d4af37" opacity={0.9} strokeWidth={0.5} className="w-full h-full" />
                </div>
            </div>

            {/* Cyan core flare */}
            <div
                className="absolute right-[12%] top-1/2 -translate-y-1/2 w-40 h-40 rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(28,229,218,0.35) 0%, rgba(252,161,18,0.18) 40%, transparent 70%)",
                    filter: "blur(10px)",
                }}
                aria-hidden="true"
            />

            {/* Vignette */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0c27]/30 to-[#0a0c27]" />

            {/* Top eyebrow line */}
            <div className="absolute top-28 left-5 md:left-10 right-5 md:right-10 flex items-center justify-between text-[11px] uppercase tracking-[0.3em] text-[#f6f1e1]/45 font-display">
                <span data-testid="hero-eyebrow-left">
                    Integrity&nbsp;Go&nbsp;//&nbsp;Sacred&nbsp;Circle
                </span>
                <span className="hidden md:inline">
                    Invitation Only · Est. 2026
                </span>
            </div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    className="font-display font-black text-[#f6f1e1] tracking-tighter leading-[0.95] text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] max-w-5xl"
                    data-testid="hero-headline"
                >
                    Grow with people who
                    <br className="hidden sm:block" /> want to see you{" "}
                    <span className="font-display font-black uppercase bg-gradient-to-br from-[#f2c652] to-[#fca112] bg-clip-text text-transparent">
                        WIN.
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mt-6 md:mt-8 max-w-2xl text-base md:text-lg text-[#f6f1e1]/70 font-body leading-relaxed"
                    data-testid="hero-subheadline"
                >
                    A private circle for professionals who lead with integrity,
                    show up with value, and grow through{" "}
                    <span className="text-[#f6f1e1] font-semibold">
                        trust-based
                    </span>{" "}
                    relationships.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-8 md:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                    <a
                        href={APPLY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-apply-cta"
                        className="group inline-flex items-center gap-3 bg-gradient-to-br from-[#f2c652] to-[#d4af37] hover:from-[#fcc001] hover:to-[#f2c652] text-[#0a0c27] font-display font-semibold text-base px-6 py-4 rounded-sm transition-all hover:-translate-y-0.5 shadow-[0_15px_50px_-12px_rgba(242,198,82,0.55)]"
                    >
                        Apply Now · $24.99/month If Approved
                        <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <a
                        href="#layers"
                        data-testid="hero-explore-link"
                        className="inline-flex items-center gap-2 text-[#1ce5da] hover:text-[#00ffff] font-body text-sm border-b border-[#1ce5da]/30 hover:border-[#00ffff] pb-1 transition-colors"
                    >
                        Or see how deep it goes ↓
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
